import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Star, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Link2, Globe, FileText, Users,
  Calendar, Mail, BarChart3, Briefcase, Truck, Heart, Bookmark, Settings2,
} from 'lucide-react';

const ICONS = {
  link: Link2, globe: Globe, star: Star, file: FileText, users: Users,
  calendar: Calendar, mail: Mail, chart: BarChart3, briefcase: Briefcase,
  truck: Truck, heart: Heart, bookmark: Bookmark,
};
const COLORS = ['#0E1944', '#E91E8C', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4'];
const EMPTY = { name: '', url: '', icon: 'link', color: '#0E1944', new_tab: true };

export function FavoritesSection() {
  const navigate = useNavigate();
  const [favs, setFavs] = useState(null);
  const [manage, setManage] = useState(false);
  const [form, setForm] = useState(null); // null | {fav?}

  const load = () => api.get('/favorites').then((r) => setFavs(r.data)).catch(() => setFavs([]));
  useEffect(() => { load(); }, []);

  const openFav = (f) => {
    const isExternal = /^https?:\/\//i.test(f.url);
    if (isExternal) {
      window.open(f.url, f.new_tab ? '_blank' : '_self', 'noopener');
    } else {
      navigate(f.url.startsWith('/') ? f.url : `/${f.url}`);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error('Name and link are required');
    try {
      if (form.id) await api.put(`/favorites/${form.id}`, { name: form.name, url: form.url, icon: form.icon, color: form.color, new_tab: form.new_tab });
      else await api.post('/favorites', form);
      toast.success(form.id ? 'Favorite updated' : 'Favorite added');
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save favorite');
    }
  };

  const remove = async (f) => {
    await api.delete(`/favorites/${f.id}`);
    toast.success('Favorite removed');
    load();
  };

  const move = async (idx, dir) => {
    const arr = [...favs];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFavs(arr);
    await api.post('/favorites/reorder', { ids: arr.map((x) => x.id) });
  };

  if (favs === null) return null;

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="favorites-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-magenta" />
          <h3 className="font-heading font-bold text-navy">My Favorites</h3>
          <span className="text-[10px] text-slate-400">{favs.length}/10</span>
        </div>
        <Button variant="outline" size="sm" onClick={() => setManage(true)} data-testid="manage-favorites-btn">
          <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Manage Favorites
        </Button>
      </div>
      {favs.length === 0 ? (
        <button
          onClick={() => { setManage(true); setForm({ ...EMPTY }); }}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-6 text-sm text-slate-400 hover:border-magenta hover:text-magenta transition"
          data-testid="add-first-favorite"
        >
          <Plus className="w-4 h-4 inline mr-1" /> Add your first quick link
        </button>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="favorites-grid">
          {favs.map((f) => {
            const Icon = ICONS[f.icon] || Link2;
            return (
              <button
                key={f.id}
                onClick={() => openFav(f)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90 hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: f.color || '#0E1944' }}
                data-testid={`favorite-btn-${f.id}`}
                title={f.url}
              >
                <Icon className="w-3.5 h-3.5" /> {f.name}
              </button>
            );
          })}
        </div>
      )}

      {manage && (
        <Dialog open onOpenChange={(v) => { if (!v) { setManage(false); setForm(null); } }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="favorites-manage-dialog">
            <DialogHeader><DialogTitle>Manage Favorites</DialogTitle></DialogHeader>
            {!form && (
              <>
                <div className="space-y-2">
                  {favs.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No favorites yet</p>}
                  {favs.map((f, i) => {
                    const Icon = ICONS[f.icon] || Link2;
                    return (
                      <div key={f.id} className="flex items-center gap-2 border border-slate-100 rounded-lg px-3 py-2" data-testid={`fav-row-${f.id}`}>
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: f.color }}>
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-navy truncate">{f.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{f.url}{f.new_tab && /^https?:/i.test(f.url) ? ' · new tab' : ''}</div>
                        </div>
                        <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-navy disabled:opacity-30" data-testid={`fav-up-${f.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => move(i, 1)} disabled={i === favs.length - 1} className="p-1 text-slate-400 hover:text-navy disabled:opacity-30" data-testid={`fav-down-${f.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setForm({ ...f })} className="p-1 text-slate-400 hover:text-navy" data-testid={`fav-edit-${f.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(f)} className="p-1 text-rose-400 hover:text-rose-600" data-testid={`fav-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
                <DialogFooter>
                  <Button className="bg-magenta hover:bg-magenta/90" disabled={favs.length >= 10} onClick={() => setForm({ ...EMPTY })} data-testid="add-favorite-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Favorite {favs.length >= 10 && '(max 10)'}
                  </Button>
                </DialogFooter>
              </>
            )}
            {form && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Button Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. AAWTA Website" data-testid="fav-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Link *</Label>
                  <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://… or internal e.g. /leads" data-testid="fav-url-input" />
                  <p className="text-[10px] text-slate-400 mt-1">Paste a full website URL, or an internal path like /leads, /calendar, /communications</p>
                </div>
                <div>
                  <Label className="text-xs">Icon</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {Object.entries(ICONS).map(([k, Icon]) => (
                      <button key={k} type="button" onClick={() => setForm({ ...form, icon: k })}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center border ${form.icon === k ? 'border-magenta bg-magenta/10 text-magenta' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        data-testid={`fav-icon-${k}`}>
                        <Icon className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Button Color</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {COLORS.map((c) => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                        className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'border-navy scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: c }} data-testid={`fav-color-${c.replace('#', '')}`} />
                    ))}
                  </div>
                </div>
                {/^https?:\/\//i.test(form.url) && (
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="checkbox" className="accent-[#C2185B]" checked={form.new_tab} onChange={(e) => setForm({ ...form, new_tab: e.target.checked })} data-testid="fav-newtab-checkbox" />
                    Open in a new browser tab
                  </label>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForm(null)}>Back</Button>
                  <Button className="bg-magenta hover:bg-magenta/90" onClick={save} data-testid="save-favorite-btn">{form.id ? 'Update' : 'Add'} Favorite</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
